import { bpsToPercent, percentNumberToBps } from '@/utils/bps';
import {
    REGIME_DEFAULTS,
    REGIME_DRIVEN_RATES,
    TAX_REGIMES,
} from '@/api/handlers/resources/settings';
import type {
    RegimeDrivenRate,
    TaxRateSet,
    TaxRates,
    TaxRegime,
} from '@/api/models';

/**
 * Pure rules and presentational config for the tax settings screen.
 *
 * Everything derived lives here so the view stays render-only, and so the
 * things that must not be got wrong — which rates a regime seeds, what the
 * earliest legal effective date is, and which error code means what — are plain
 * functions with tests rather than JSX.
 */

// ── regimes ─────────────────────────────────────────────────────────────────

/** i18n key for a regime's name. */
export const regimeLabelKey = (regime: TaxRegime): string =>
    `settings.regimes.${regime}.name`;

/** i18n key for the one-line description under a regime. */
export const regimeDescriptionKey = (regime: TaxRegime): string =>
    `settings.regimes.${regime}.description`;

export const REGIME_OPTIONS = TAX_REGIMES;

// ── rate fields ─────────────────────────────────────────────────────────────

/**
 * Every rate the screen shows, in order, with what reads it.
 *
 * `isRegimeSeeded` splits the two distribution rates from the statutory ones.
 * The distinction is about **where the starting value comes from**, not about
 * whether the field can be edited: picking a regime fills those two in, and the
 * user may then change them. Payroll and VAT rates are statutory, identical
 * across regimes, and a regime change must never move them — a Virtual Zone
 * company still withholds 20% PIT from its staff.
 */
export type RateField = {
    key: keyof TaxRates;
    labelKey: string;
    /** Plain-language note on what this rate actually affects. */
    affectsKey: string;
    isRegimeSeeded: boolean;
};

export const RATE_FIELDS: RateField[] = [
    {
        key: 'corporateIncomeTaxBps',
        labelKey: 'settings.rates.corporateIncomeTax',
        affectsKey: 'settings.rates.corporateIncomeTaxAffects',
        isRegimeSeeded: true,
    },
    {
        key: 'dividendWithholdingTaxBps',
        labelKey: 'settings.rates.dividendWithholdingTax',
        affectsKey: 'settings.rates.dividendWithholdingTaxAffects',
        isRegimeSeeded: true,
    },
    {
        key: 'personalIncomeTaxBps',
        labelKey: 'settings.rates.personalIncomeTax',
        affectsKey: 'settings.rates.personalIncomeTaxAffects',
        isRegimeSeeded: false,
    },
    {
        key: 'employeePensionBps',
        labelKey: 'settings.rates.employeePension',
        affectsKey: 'settings.rates.employeePensionAffects',
        isRegimeSeeded: false,
    },
    {
        key: 'employerPensionBps',
        labelKey: 'settings.rates.employerPension',
        affectsKey: 'settings.rates.employerPensionAffects',
        isRegimeSeeded: false,
    },
    {
        key: 'statePensionBps',
        labelKey: 'settings.rates.statePension',
        affectsKey: 'settings.rates.statePensionAffects',
        isRegimeSeeded: false,
    },
    {
        key: 'vatStandardBps',
        labelKey: 'settings.rates.vatStandard',
        affectsKey: 'settings.rates.vatStandardAffects',
        isRegimeSeeded: false,
    },
];

export const REGIME_SEEDED_FIELDS = RATE_FIELDS.filter(
    (field) => field.isRegimeSeeded,
);
export const STATUTORY_FIELDS = RATE_FIELDS.filter(
    (field) => !field.isRegimeSeeded,
);

// ── basis points ↔ the input's percent value ────────────────────────────────

/**
 * The rate inputs hold a **plain percent number**: `15` means 15%.
 *
 * ## Why not `formatOptions={{ style: 'percent' }}`
 *
 * A percent-formatted `NumberField` puts the `%` **inside the editable text**
 * and holds a fraction (`0.15` renders as `15%`). Both halves of that are a
 * problem, and the first is a real defect: because the symbol is part of the
 * string the caret can sit after it, so typing into a field showing `8%`
 * produces `8%79` — a value that is neither rejected nor parsed, just left
 * sitting in the input. The symbol has to live outside the editable text, so
 * the field formats as a plain decimal and the view renders a static `%`
 * suffix next to it.
 *
 * That leaves two representations, each with one job:
 *
 * | | used by | 20% is |
 * | --- | --- | --- |
 * | basis points | this client's model and every calculation | `2000` |
 * | percent | the rate inputs, and the wire | `20` / `20.00` |
 *
 * The field is capped at two decimal places, which is exactly one basis point —
 * so every value the input can hold is a whole number of basis points and the
 * conversion is lossless both ways. The rounding below repairs binary-float
 * error (`15.23 * 100` is not exactly `1523`); it does not discard precision
 * the user could have entered.
 */
export const bpsToPercentValue = bpsToPercent;

/**
 * Re-exported from `@/utils/bps` (BOS-171). It had its own
 * `Math.round(percent * 100)`, which is the same conversion written a third
 * time — see {@link percentNumberToBps} for why one copy matters.
 */
export const percentValueToBps = percentNumberToBps;

/** The maximum rate the input accepts: 100%. */
export const MAX_RATE_PERCENT = 100;

/** One percentage point, the stepper's increment. */
export const RATE_STEP_PERCENT = 1;

/**
 * Two decimal places of percent — one basis point.
 *
 * Passed to the NumberField as `maximumFractionDigits`, which is what actually
 * prevents finer input reaching the server. The server rejects anything finer
 * rather than rounding it, so a client that let it be typed would produce a 400
 * the user cannot act on.
 */
export const RATE_PERCENT_DECIMALS = 2;

/**
 * A stored basis-point rate as display percent text, localized.
 *
 * Read-only figures only — history rows, the date lookup, the "regime's figure
 * is…" hint. Here `style: 'percent'` is exactly right: the text is never
 * edited, so there is no caret to land on the wrong side of the symbol.
 */
export const formatBpsPercent = (bps: number, locale: string): string =>
    new Intl.NumberFormat(locale, {
        style: 'percent',
        maximumFractionDigits: RATE_PERCENT_DECIMALS,
    }).format(bps / 10_000);

// ── the form's derived state ────────────────────────────────────────────────

/**
 * The rate inputs, as plain percent numbers (15 means 15%).
 *
 * `null` is a cleared field. It is distinct from `0`, which is a legitimate
 * rate (both zone regimes set CIT to exactly that), so an empty field can never
 * be silently saved as zero.
 */
export type RateDraft = Record<keyof TaxRates, number | null>;

export const toRateDraft = (rates: TaxRates): RateDraft => ({
    corporateIncomeTaxBps: bpsToPercentValue(rates.corporateIncomeTaxBps),
    dividendWithholdingTaxBps: bpsToPercentValue(
        rates.dividendWithholdingTaxBps,
    ),
    personalIncomeTaxBps: bpsToPercentValue(rates.personalIncomeTaxBps),
    employeePensionBps: bpsToPercentValue(rates.employeePensionBps),
    employerPensionBps: bpsToPercentValue(rates.employerPensionBps),
    statePensionBps: bpsToPercentValue(rates.statePensionBps),
    vatStandardBps: bpsToPercentValue(rates.vatStandardBps),
});

/** The empty draft rendered before the first load resolves. */
export const EMPTY_DRAFT: RateDraft = {
    corporateIncomeTaxBps: null,
    dividendWithholdingTaxBps: null,
    personalIncomeTaxBps: null,
    employeePensionBps: null,
    employerPensionBps: null,
    statePensionBps: null,
    vatStandardBps: null,
};

/**
 * A draft → the rates to send, or `null` when any field is empty or out of
 * range.
 *
 * Returning null rather than coercing is the point: a cleared field must block
 * the save, not become a zero rate.
 */
export const draftToRates = (draft: RateDraft): TaxRates | null => {
    const rates = {} as TaxRates;
    for (const field of RATE_FIELDS) {
        const value = draft[field.key];
        if (value === null || !Number.isFinite(value)) {
            return null;
        }
        const bps = percentValueToBps(value);
        if (bps < 0 || bps > 10_000) {
            return null;
        }
        rates[field.key] = bps;
    }
    return rates;
};

/** i18n key for a field's problem, or null when it is fine. */
export const rateFieldError = (value: number | null): string | null => {
    if (value === null || !Number.isFinite(value)) {
        return 'settings.errors.required';
    }
    const bps = percentValueToBps(value);
    return bps < 0 || bps > 10_000 ? 'settings.errors.range' : null;
};

/**
 * The draft a regime change produces: the regime's two rates replace what was
 * there, and **every statutory rate is left exactly as it was**.
 *
 * That asymmetry is the point. Payroll and VAT do not vary by regime, so a
 * regime switch that silently restated the PIT rate would be a data-loss bug
 * wearing a feature's clothes.
 */
export const applyRegimeDefaults = (
    regime: TaxRegime,
    current: RateDraft,
): RateDraft => {
    const defaults = REGIME_DEFAULTS[regime];
    const next = { ...current };
    for (const key of REGIME_DRIVEN_RATES) {
        next[key] = bpsToPercentValue(defaults[key]);
    }
    return next;
};

/** Whether a draft rate differs from what the chosen regime would set. */
export const isOverride = (
    regime: TaxRegime,
    key: RegimeDrivenRate,
    value: number | null,
): boolean =>
    value === null
        ? false
        : percentValueToBps(value) !== REGIME_DEFAULTS[regime][key];

/** The regime's figure for one rate, in basis points (for the hint text). */
export const regimeDefaultBps = (
    regime: TaxRegime,
    key: RegimeDrivenRate,
): number => REGIME_DEFAULTS[regime][key];

/**
 * Has anything actually changed against the set this edit is based on?
 *
 * The note and the effective date are deliberately **not** part of this. A
 * change with only a note is not a rate change, and the date always differs
 * from the base set's by construction — including either would make Save live
 * permanently.
 */
export const isDirty = (
    base: TaxRateSet | null,
    regime: TaxRegime,
    draft: RateDraft,
): boolean => {
    if (!base) {
        // Nothing recorded yet: any complete draft is a change worth saving.
        return draftToRates(draft) !== null;
    }
    if (base.regime !== regime) {
        return true;
    }
    return RATE_FIELDS.some((field) => {
        const value = draft[field.key];
        return value === null
            ? true
            : percentValueToBps(value) !== base.rates[field.key];
    });
};

// ── effective dates ─────────────────────────────────────────────────────────

/** `yyyy-MM-dd` for today, in the local calendar. */
export const todayIso = (): string => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${now.getFullYear()}-${month}-${day}`;
};

/** `yyyy-MM-dd` one day on. Uses UTC so a DST boundary can't skip a day. */
export const addOneDay = (iso: string): string => {
    const [year = 0, month = 1, day = 1] = iso.split('-').map(Number);
    const next = new Date(Date.UTC(year, month - 1, day + 1));
    return next.toISOString().slice(0, 10);
};

/**
 * The earliest date a new change may take effect.
 *
 * A new set must be dated **strictly after every existing set**, including
 * future-dated ones — that is what keeps the timeline monotonic. So the floor
 * is the day after the latest recorded `effectiveFrom`, and it is `null` only
 * when nothing has been recorded at all.
 */
export const earliestEffectiveFrom = (
    latest: TaxRateSet | null,
): string | null => (latest ? addOneDay(latest.effectiveFrom) : null);

/**
 * The date the field should start on: today when that is still legal, and the
 * earliest legal day otherwise.
 *
 * This is the whole of "design around the date constraint, don't just report
 * it". After a future-dated change is scheduled, today is no longer a legal
 * choice — so the field opens on the first day that is, and the user is told
 * why rather than being bounced by a 400 they cannot act on.
 */
export const defaultEffectiveFrom = (
    latest: TaxRateSet | null,
    today: string = todayIso(),
): string => {
    const earliest = earliestEffectiveFrom(latest);
    if (!earliest) {
        return today;
    }
    return earliest > today ? earliest : today;
};

/** Whether a chosen date clears the floor. Validated before submitting. */
export const isEffectiveFromValid = (
    value: string,
    latest: TaxRateSet | null,
): boolean => {
    if (!value) {
        return false;
    }
    const earliest = earliestEffectiveFrom(latest);
    return earliest === null || value >= earliest;
};

// ── notes ───────────────────────────────────────────────────────────────────

/** The server's cap. Enforced here so a long note is not a round trip. */
export const NOTE_MAX_LENGTH = 500;

// ── client ids ──────────────────────────────────────────────────────────────

/**
 * A client-generated id for a rate set, doubling as the idempotency key.
 *
 * Generated once per pending edit and reused across retries, so a save that
 * times out and is retried returns the recorded set rather than a duplicate.
 * Capped at 36 characters, which the server enforces — the fallback stays well
 * inside it.
 */
export const newTaxRateSetId = (): string =>
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `trs-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;

// ── error codes ─────────────────────────────────────────────────────────────

/**
 * Error code → the message the user gets.
 *
 * Every one of these has a specific, actionable remedy, which is why a generic
 * failure toast is not good enough: "someone else changed the rates, reload and
 * re-apply" and "pick a different date" are different problems with different
 * next steps.
 */
const SAVE_ERROR_KEYS: Record<string, string> = {
    EFFECTIVE_FROM_NOT_AFTER_LATEST:
        'settings.saveErrors.effectiveFromNotAfterLatest',
    STALE_BASE_SET: 'settings.saveErrors.staleBaseSet',
    DATE_ALREADY_RECORDED: 'settings.saveErrors.dateAlreadyRecorded',
    ID_REUSED_WITH_DIFFERENT_CONTENT: 'settings.saveErrors.idReused',
};

/** The i18n key for a save failure, falling back to a generic message. */
export const saveErrorKey = (code: string | undefined): string =>
    (code && SAVE_ERROR_KEYS[code]) || 'settings.saveErrors.unknown';

/**
 * Whether a save failure is recoverable **in place** — the user's edits are
 * still valid, something about the world moved underneath them.
 *
 * `STALE_BASE_SET` is the case that matters: another writer recorded a change,
 * so the base set is out of date, but everything typed is still what the user
 * wants. Offering "reload and re-apply" keeps it; discarding the form would
 * punish them for someone else's timing.
 */
export const isRecoverableInPlace = (code: string | undefined): boolean =>
    code === 'STALE_BASE_SET';

/**
 * Whether a save failure is fixed by regenerating the client id and retrying.
 *
 * `ID_REUSED_WITH_DIFFERENT_CONTENT` means this client's idempotency key was
 * already used for different content — a fresh id resolves it, and the user
 * should never have to retype anything.
 */
export const needsNewClientId = (code: string | undefined): boolean =>
    code === 'ID_REUSED_WITH_DIFFERENT_CONTENT';
